import { useI18n } from '@platform/i18n';
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { api } from './api';
import type { RegionalPreferences } from './api/calendar-extra';
import { UserMenu, useErrorText } from '@platform/ui';
import type { ComponentProps } from 'react';
import { CalendarContext } from './calendar/calendar-context';

interface RegionalContextValue {
  value: RegionalPreferences | undefined;
  error: unknown;
  reload: () => void;
  save: (value: RegionalPreferences) => Promise<void>;
}
const RegionalContext = createContext<RegionalContextValue | null>(null);

/** Authenticated account state: no browser persistence and no writes during loading. */
export function RegionalProvider({ children, onSaved }: { children: ReactNode; onSaved: (value: RegionalPreferences) => void }) {
  const { applyLocale } = useI18n();
  const mounted = useRef(true);
  const [value, setValue] = useState<RegionalPreferences>();
  const [error, setError] = useState<unknown>();
  const [attempt, setAttempt] = useState(0);
  const reload = useCallback(() => setAttempt((current) => current + 1), []);
  useEffect(() => {
    mounted.current = true;
    let cancelled = false;
    api.regionalSettings().then((next) => {
      if (!cancelled) { setValue(next); setError(undefined); applyLocale(next.locale); }
    }).catch((failure: unknown) => { if (!cancelled) setError(failure); });
    return () => { cancelled = true; mounted.current = false; applyLocale(null); };
  }, [applyLocale, attempt]);
  const save = async (next: RegionalPreferences) => {
    const saved = await api.saveRegionalSettings(next);
    if (!mounted.current) return;
    setValue(saved);
    applyLocale(saved.locale);
    onSaved(saved);
  };
  return <RegionalContext.Provider value={{ value, error, reload, save }}>{children}</RegionalContext.Provider>;
}

export function useRegionalSettings() {
  const context = useContext(RegionalContext);
  if (!context) throw new Error('Regional settings require the authenticated shell');
  return context;
}

/** Webmail's avatar language choice follows the account; admin keeps its browser preference. */
export function RegionalUserMenu(props: ComponentProps<typeof UserMenu>) {
  const { value, save } = useRegionalSettings();
  const calendar = useContext(CalendarContext);
  const errors = useErrorText();
  return <UserMenu {...props} languageDisabled={!value} onLocaleChange={(locale) => {
    if (!value) return;
    const next = calendar?.settingsLoaded ? { ...value, displayTimeZone: calendar.settings.displayTimeZone, weekNumbering: calendar.settings.weekNumbering } : value;
    void save({ ...next, locale }).catch((error: unknown) => errors.notify(error));
  }} />;
}
